import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from '@aus-platform/design-system';
import type {
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
import type { GeotagTableRowData } from '../geotag-images-modal/types';
import { CarouselStep, DatasetType, GeotagImagesModalView } from '../enums';
import type {
  GeotagImageListQueryParams,
  GeotagImageObj,
} from 'src/shared/api';
import { useGetGeotagImageListRequest } from 'src/shared/api';
import { EPSGCode } from 'shared/enums';
import type {
  GeotagImagesContextProps,
  GeotagImagesProviderProps,
} from './types';
import {
  GEOTAG_TABLE_PAGINATION_INITIAL_STATE,
  GEOTAG_TABLE_PAGE_SIZE_OPTIONS,
} from '../geotag-images-modal/constants';
import { convertTanStackSortingToApiParam } from '../helpers';

const GeotagImagesContext = createContext<GeotagImagesContextProps | undefined>(
  undefined,
);

// Field mapping for geotag images sorting.
const GeotagSortingFieldMapping: Record<string, string> = {
  filename: 'filename',
  xCoordinate: 'x_coordinate',
  yCoordinate: 'y_coordinate',
  zCoordinate: 'z_coordinate',
};

export const GeotagImagesProvider: React.FC<GeotagImagesProviderProps> = ({
  dataset,
  datasetType,
  onUploadImages,
  displayUploadGeotagsFile,
  onOpenGeotagGeneration,
  children,
}) => {
  // States
  const [modalPageView, setModalPageView] = useState<GeotagImagesModalView>(
    GeotagImagesModalView.Table,
  );

  // Currently the pageinationInitialState is used to set the initial state of the pagination props
  // This value comes from the design system, instead this should be implementation specific
  // and hence is added here with custom value of GEOTAG_TABLE_PAGINATION_INITIAL_STATE
  const [paginationProps, setPaginationProps] = useState<PaginationState>(
    GEOTAG_TABLE_PAGINATION_INITIAL_STATE,
  );
  const [sortingProps, setSortingProps] = useState<SortingState>([]);
  const [pageCount, setPageCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [geotagImages, setGeotagImages] = useState<GeotagTableRowData[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionSelectedGeotagImage, setActionSelectedGeotagImage] =
    useState<GeotagImageObj>();
  const [isCRSGeographic, setIsCRSGeographic] = useState(
    dataset.geotagHorizontalCrs === EPSGCode.WGS84,
  );
  const [showToggleGeotagImagesModal, setShowToggleGeotagImagesModal] =
    useState(false);
  const [showEditGeotagImagesModal, setShowEditGeotagImagesModal] =
    useState(false);
  const [geotagImagePayload, setGeotagImagePayload] =
    useState<GeotagImageListQueryParams>({
      search: '',
      imagesWithGeotags: false,
      imagesWithoutGeotags: false,
      geotagsWithoutImages: false,
    });

  const {
    data: geotagImagesResponse,
    isLoading,
    isSuccess,
    isError,
    refetch: refetchGeotagImages,
  } = useGetGeotagImageListRequest({
    pageNumber: paginationProps.pageIndex,
    pageSize: paginationProps.pageSize,
    sortBy: convertTanStackSortingToApiParam(
      sortingProps,
      GeotagSortingFieldMapping,
    ),
    ...geotagImagePayload,
    ...(datasetType == DatasetType.IterationDataset && {
      iterationDataset: dataset.id,
    }),
    ...(datasetType == DatasetType.MergedDataset && {
      mergedDataset: dataset.id,
    }),
  });

  useEffect(() => {
    if (geotagImagesResponse && isSuccess) {
      setPageCount(
        Math.ceil(geotagImagesResponse.data.total / paginationProps.pageSize),
      );
      const startingSno =
        paginationProps.pageIndex * paginationProps.pageSize + 1;
      const geotagImagesWithSno = geotagImagesResponse.data.geotagImages.map(
        (image, index) => ({
          ...image,
          sno: startingSno + index,
        }),
      );
      setRowSelection({});
      setGeotagImages(geotagImagesWithSno);
    }

    if (isError) {
      toast.error('Error loading geotag images');
    }
  }, [
    geotagImagesResponse,
    isSuccess,
    isLoading,
    paginationProps.pageIndex,
    paginationProps.pageSize,
    sortingProps,
  ]);

  const showDeleteGeotagsModal = () => setShowDeleteModal(true);
  const hideDeleteGeotagsModal = () => setShowDeleteModal(false);

  const getSteppedGeotagImageItem = (step: CarouselStep, id: string) => {
    let updatedIndex = 0;
    const currentIndex = geotagImages.findIndex((item) => item.id === id);

    if (currentIndex >= 0) {
      updatedIndex =
        step === CarouselStep.Next ? currentIndex + 1 : currentIndex - 1;
      if (updatedIndex >= geotagImages.length) {
        updatedIndex = 0;
      }
      if (updatedIndex < 0) {
        updatedIndex = geotagImages.length - 1 || 0;
      }
    }

    const steppedGeotagImage = geotagImages.find(
      (gi) => gi.sno === updatedIndex + 1,
    );

    setActionSelectedGeotagImage(steppedGeotagImage);
  };

  const value = {
    geotagImages,
    geotagImagePayload,
    paginationProps,
    isCRSGeographic,
    setIsCRSGeographic,
    rowSelection,
    showToggleGeotagImagesModal,
    showEditGeotagImagesModal,
    setShowEditGeotagImagesModal,
    actionSelectedGeotagImage,
    setActionSelectedGeotagImage,
    setRowSelection,
    setPaginationProps,
    setShowToggleGeotagImagesModal,
    setGeotagImagePayload,
    onUploadImages,
    refetchGeotagImages,
    displayUploadGeotagsFile,
    onOpenGeotagGeneration,
    showDeleteModal,
    modalPageView,
    setModalPageView,
    showDeleteGeotagsModal,
    hideDeleteGeotagsModal,
    getSteppedGeotagImageItem,
    pageCount,
    isLoading,
    searchText,
    setSearchText,
    GEOTAG_TABLE_PAGE_SIZE_OPTIONS,
    sortingProps,
    setSortingProps,
  };

  return (
    <GeotagImagesContext.Provider value={value}>
      {children}
    </GeotagImagesContext.Provider>
  );
};

export const useGeotagImagesContext = () => {
  const context = useContext(GeotagImagesContext);
  if (!context) {
    throw new Error(
      'useGeotagImagesContext must be used within a GeotagImagesProvider',
    );
  }
  return context;
};
